import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { quizAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import QuizSubmissions from '../components/QuizSubmissions';

function QuizDetails() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { quizId } = useParams();
  const { showToast } = useToast();
  
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingQuiz, setEditingQuiz] = useState(false);

  // Verify user is admin
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      // If quiz was passed via location state, use it
      if (location.state?.quiz) {
        setQuiz(location.state.quiz);
        setLoading(false);
        return;
      }

      // Otherwise fetch from API
      const response = await quizAPI.getQuizById(quizId);
      setQuiz(response.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
      showToast(error.response?.data?.message || 'Failed to load quiz', 'error');
      if (error.response?.status === 403 || error.response?.status === 404) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This will also delete all submissions. This action cannot be undone.')) {
      return;
    }

    try {
      await quizAPI.deleteQuiz(quizId);
      showToast('Quiz deleted successfully', 'success');
      navigate('/admin');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete quiz', 'error');
    }
  };

  const handleEdit = () => {
    setEditingQuiz(true);
  };

  const handleUpdateQuiz = async (quizId, updatedData) => {
    try {
      await quizAPI.updateQuiz(quizId, updatedData);
      showToast('Quiz updated successfully', 'success');
      setEditingQuiz(false);
      // Refresh quiz data
      fetchQuiz();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update quiz', 'error');
    }
  };

  const handleBack = () => {
    navigate('/admin/quizzes');
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '18px', color: '#667eea' }}>Loading quiz...</div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '18px', color: '#e53e3e' }}>Quiz not found</div>
        <button 
          className="btn btn-secondary" 
          onClick={handleBack}
          style={{ marginTop: '20px' }}
        >
          ← Back to Quizzes
        </button>
      </div>
    );
  }

  return (
    <QuizSubmissions 
      quiz={quiz} 
      onBack={handleBack}
      onDelete={handleDelete}
      onEdit={handleEdit}
      editingQuiz={editingQuiz}
      onUpdateQuiz={handleUpdateQuiz}
      onCloseEdit={() => setEditingQuiz(false)}
      filterStudentId={location.state?.filterStudentId}
    />
  );
}

export default QuizDetails;
